import { useState, useEffect, useRef } from 'react';
import { Link, FileText, Edit, ChevronUp, ChevronDown, Trash2, ExternalLink, Book, UploadCloud, Plus, FolderOpen, RefreshCw, AlertCircle, CheckCircle2, Clock, X, Eye, Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import {
  fetchKnowledgeBase,
  ingestUrls,
  crawlBaseUrl,
  uploadFile,
  deleteKnowledgeBaseEntries,
  downloadKnowledgeBaseFile
} from '../services/api';

// Constants
const TYPE_ICONS = {
  url: Link,
  document: FileText,
  text: Edit,
  default: FileText
};

const STATUS_CONFIG = {
  completed: { color: 'text-green-400 bg-green-400/10', icon: CheckCircle2 },
  success: { color: 'text-green-400 bg-green-400/10', icon: CheckCircle2 },
  pending: { color: 'text-yellow-400 bg-yellow-400/10', icon: Clock },
  failed: { color: 'text-red-400 bg-red-400/10', icon: AlertCircle },
  error: { color: 'text-red-400 bg-red-400/10', icon: AlertCircle },
  processing: { color: 'text-blue-400 bg-blue-400/10', icon: RefreshCw },
  default: { color: 'text-gray-400 bg-gray-400/10', icon: AlertCircle }
};

const PDF_SIZE_LIMITS = {
  MAX_SIZE: 10 * 1024 * 1024, // 10MB
  TARGET_COMPRESSION_SIZE: 1 * 1024 * 1024 // 1MB
};

const PDF_COMPRESSION_SETTINGS = {
  MAX_ATTEMPTS: 3,
  SCALE_REDUCTION_FACTOR: 0.8,
  MIN_SCALE: 0.4,
  QUALITY_REDUCTION_FACTOR: 0.7,
  MIN_QUALITY: 0.3
};

// Utility functions
const getTypeIcon = (type) => TYPE_ICONS[type] || TYPE_ICONS.default;
const getStatusConfig = (status) => STATUS_CONFIG[status?.toLowerCase()] || STATUS_CONFIG.default;
const formatFileSize = (bytes) => {
  if (!bytes) return 'N/A';
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

// Reusable Alert Component
const CustomAlert = ({ isOpen, onClose, type = 'info', title, message, showConfirm = false, onConfirm }) => {
  if (!isOpen) return null;

  const alertStyles = {
    success: { bg: 'bg-gray-800 border-green-500', icon: CheckCircle2, iconColor: 'text-green-400', titleColor: 'text-green-300', iconBg: 'bg-green-500/20' },
    error: { bg: 'bg-gray-800 border-red-500', icon: AlertCircle, iconColor: 'text-red-400', titleColor: 'text-red-300', iconBg: 'bg-red-500/20' },
    warning: { bg: 'bg-gray-800 border-orange-500', icon: AlertCircle, iconColor: 'text-orange-400', titleColor: 'text-orange-300', iconBg: 'bg-orange-500/20' },
    info: { bg: 'bg-gray-800 border-blue-500', icon: AlertCircle, iconColor: 'text-blue-400', titleColor: 'text-blue-300', iconBg: 'bg-blue-500/20' }
  };

  const style = alertStyles[type] || alertStyles.info;
  const Icon = style.icon;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className={`${style.bg} rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl`}>
        <div className="flex items-start space-x-4">
          <div className={`${style.iconBg} rounded-full p-2 flex-shrink-0`}>
            <Icon size={20} className={style.iconColor} />
          </div>
          <div className="flex-1 min-w-0">
            {title && <h3 className={`font-semibold ${style.titleColor} mb-2 text-lg`}>{title}</h3>}
            <p className="text-gray-200 text-sm leading-relaxed">{message}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-1 rounded-full hover:bg-gray-700">
            <X size={18} />
          </button>
        </div>
        
        <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-700">
          {showConfirm ? (
            <>
              <button onClick={onClose} className="px-5 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium">
                Cancel
              </button>
              <button onClick={() => { onConfirm(); onClose(); }} className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium shadow-lg">
                Confirm
              </button>
            </>
          ) : (
            <button onClick={onClose} className={`px-5 py-2.5 rounded-lg transition-colors font-medium shadow-lg ${
              type === 'success' ? 'bg-green-600 hover:bg-green-700' :
              type === 'error' ? 'bg-red-600 hover:bg-red-700' :
              type === 'warning' ? 'bg-orange-600 hover:bg-orange-700' :
              'bg-blue-600 hover:bg-blue-700'
            } text-white`}>
              OK
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Rich Text Editor with PDF Generation - FIXED CURSOR POSITION ISSUE
const RichTextEditor = ({ content, setContent, placeholder = "Enter or paste your rich text content here..." }) => {
  const editorRef = useRef(null);
  const [showToolbar, setShowToolbar] = useState(false);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load PDF libraries
  useEffect(() => {
    const loadScript = (src) => {
      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = resolve;
        document.body.appendChild(script);
      });
    };

    Promise.all([
      loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js'),
      loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js')
    ]).then(() => setScriptsLoaded(true));
  }, []);

  // Initialize content only once
  useEffect(() => {
    const editor = editorRef.current;
    if (editor && !isInitialized && content) {
      editor.innerHTML = content;
      setIsInitialized(true);
    }
  }, [content, isInitialized]);

  // Save and restore cursor position
  const saveCursorPosition = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(editorRef.current);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      return preCaretRange.toString().length;
    }
    return 0;
  };

  const restoreCursorPosition = (position) => {
    const editor = editorRef.current;
    if (!editor) return;
    
    const walker = document.createTreeWalker(
      editor,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    let currentPosition = 0;
    let targetNode = null;
    let targetOffset = 0;

    while (walker.nextNode()) {
      const node = walker.currentNode;
      const nodeLength = node.textContent.length;
      
      if (currentPosition + nodeLength >= position) {
        targetNode = node;
        targetOffset = position - currentPosition;
        break;
      }
      currentPosition += nodeLength;
    }

    if (targetNode) {
      const range = document.createRange();
      const selection = window.getSelection();
      range.setStart(targetNode, Math.min(targetOffset, targetNode.textContent.length));
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const handleInput = () => {
      const cursorPosition = saveCursorPosition();
      const newContent = editor.innerHTML;
      setContent(newContent);
      
      // Restore cursor position after content update
      setTimeout(() => {
        restoreCursorPosition(cursorPosition);
      }, 0);
    };

    const handlePaste = (e) => {
      e.preventDefault();
      const cursorPosition = saveCursorPosition();
      const clipboardData = e.clipboardData || window.clipboardData;
      const htmlData = clipboardData.getData('text/html');
      const textData = clipboardData.getData('text/plain');
      
      if (htmlData) {
        document.execCommand('insertHTML', false, cleanHtmlContent(htmlData));
      } else if (textData) {
        document.execCommand('insertText', false, textData);
      }
      
      const newContent = editor.innerHTML;
      setContent(newContent);
      
      // Restore cursor position after paste
      setTimeout(() => {
        restoreCursorPosition(cursorPosition + (textData?.length || 0));
      }, 0);
    };

    const handleFocus = () => setShowToolbar(true);
    const handleBlur = (e) => {
      setTimeout(() => {
        if (!e.relatedTarget || !e.relatedTarget.closest('.toolbar')) {
          setShowToolbar(false);
        }
      }, 100);
    };

    editor.addEventListener('input', handleInput);
    editor.addEventListener('paste', handlePaste);
    editor.addEventListener('focus', handleFocus);
    editor.addEventListener('blur', handleBlur);

    return () => {
      editor.removeEventListener('input', handleInput);
      editor.removeEventListener('paste', handlePaste);
      editor.removeEventListener('focus', handleFocus);
      editor.removeEventListener('blur', handleBlur);
    };
  }, [setContent]);

  const cleanHtmlContent = (html) => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    
    // Remove harmful elements
    temp.querySelectorAll('script, link, meta, style').forEach(el => el.remove());
    
    // Clean CSS properties
    const cleanCSSProperties = (styleStr) => {
      if (!styleStr) return '';
      const properties = styleStr.split(';').filter(prop => prop.trim());
      const safeProperties = ['color', 'background-color', 'font-weight', 'font-style', 'text-decoration', 'font-size', 'font-family', 'line-height', 'text-align', 'margin', 'padding', 'border', 'border-color', 'border-width', 'border-style'];
      
      return properties.filter(property => {
        const [key, value] = property.split(':').map(str => str.trim());
        return key && value && safeProperties.includes(key.toLowerCase()) && !value.includes('oklch(') && !value.includes('lab(') && !value.includes('lch(') && !value.includes('color(') && !value.includes('hwb(');
      }).join('; ');
    };

    // Clean all elements
    temp.querySelectorAll('*').forEach(el => {
      el.removeAttribute('class');
      const style = el.getAttribute('style');
      if (style) {
        const cleanedStyle = cleanCSSProperties(style);
        if (cleanedStyle) {
          el.setAttribute('style', cleanedStyle);
        } else {
          el.removeAttribute('style');
        }
      }
    });

    return temp.innerHTML;
  };

  const formatText = (command, value = null) => {
    const cursorPosition = saveCursorPosition();
    document.execCommand(command, false, value);
    editorRef.current.focus();
    
    const newContent = editorRef.current.innerHTML;
    setContent(newContent);
    
    // Restore cursor position after formatting
    setTimeout(() => {
      restoreCursorPosition(cursorPosition);
    }, 0);
  };

  const generatePdf = async (htmlString, compressionOptions = {}) => {
    const { jsPDF } = window.jspdf;
    const html2canvas = window.html2canvas;
    if (!html2canvas || !jsPDF) throw new Error("PDF generation libraries not loaded yet.");

    const { scale = 2, quality = 1.0, compress = false } = compressionOptions;
    const printContainer = document.createElement('div');
    document.body.appendChild(printContainer);

    printContainer.style.cssText = 'position: absolute; left: -9999px; width: 210mm; padding: 1in; box-sizing: border-box; background: white;';
    printContainer.innerHTML = `<div style="font-family: Arial, sans-serif; line-height: 1.6; font-size: 12pt; color: black;">${htmlString || '<p>No content to generate.</p>'}</div>`;

    try {
      const canvas = await html2canvas(printContainer, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        quality: quality
      });

      document.body.removeChild(printContainer);
      const imgData = compress ? canvas.toDataURL('image/jpeg', quality) : canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: compress });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgPdfHeight = (pdfWidth - 20) / (canvas.width / canvas.height);
      let heightLeft = imgPdfHeight;
      let position = 10;

      pdf.addImage(imgData, compress ? 'JPEG' : 'PNG', 10, position, pdfWidth - 20, imgPdfHeight);
      heightLeft -= (pdfHeight - 20);

      while (heightLeft > 0) {
        position = -heightLeft - 10;
        pdf.addPage();
        pdf.addImage(imgData, compress ? 'JPEG' : 'PNG', 10, position, pdfWidth - 20, imgPdfHeight);
        heightLeft -= (pdfHeight - 20);
      }

      return pdf;
    } catch (error) {
      document.body.removeChild(printContainer);
      throw error;
    }
  };

  const compressPdf = async (htmlString, targetSize = PDF_SIZE_LIMITS.TARGET_COMPRESSION_SIZE) => {
    let currentScale = 2;
    let currentQuality = 1.0;
    let attempt = 1;
    let bestPdf = null;
    let bestSize = Infinity;
    
    while (attempt <= PDF_COMPRESSION_SETTINGS.MAX_ATTEMPTS) {
      try {
        const pdf = await generatePdf(htmlString, { scale: currentScale, quality: currentQuality, compress: true });
        const pdfBlob = pdf.output('blob');
        const pdfSize = pdfBlob.size;

        if (pdfSize < bestSize) {
          bestPdf = pdf;
          bestSize = pdfSize;
        }

        if (pdfSize <= targetSize) {
          return { pdf: bestPdf, size: bestSize, compressed: true, attempts: attempt };
        }

        currentScale = Math.max(currentScale * PDF_COMPRESSION_SETTINGS.SCALE_REDUCTION_FACTOR, PDF_COMPRESSION_SETTINGS.MIN_SCALE);
        currentQuality = Math.max(currentQuality * PDF_COMPRESSION_SETTINGS.QUALITY_REDUCTION_FACTOR, PDF_COMPRESSION_SETTINGS.MIN_QUALITY);
        attempt++;
        
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error(`Compression attempt ${attempt} failed:`, error);
        break;
      }
    }

    return { pdf: bestPdf, size: bestSize, compressed: bestSize < (targetSize * 2), attempts: attempt - 1 };
  };

  RichTextEditor.generatePdf = (content) => {
    if (!scriptsLoaded) throw new Error("PDF libraries not loaded yet");
    return generatePdf(content);
  };

  RichTextEditor.compressPdf = (content, targetSize) => {
    if (!scriptsLoaded) throw new Error("PDF libraries not loaded yet");
    return compressPdf(content, targetSize);
  };

  const toolbarButtons = [
    { command: 'bold', icon: Bold, title: 'Bold' },
    { command: 'italic', icon: Italic, title: 'Italic' },
    { command: 'underline', icon: Underline, title: 'Underline' },
    null, // separator
    { command: 'justifyLeft', icon: AlignLeft, title: 'Align Left' },
    { command: 'justifyCenter', icon: AlignCenter, title: 'Align Center' },
    { command: 'justifyRight', icon: AlignRight, title: 'Align Right' }
  ];

  return (
    <div className="relative">
      {showToolbar && (
        <div className="toolbar absolute -top-12 left-0 bg-gray-600 border border-gray-500 rounded-lg shadow-lg p-2 flex gap-1 z-10">
          {toolbarButtons.map((button, index) => 
            button ? (
              <button
                key={button.command}
                onClick={() => formatText(button.command)}
                className="p-2 hover:bg-gray-500 rounded transition-colors text-white"
                title={button.title}
                type="button"
              >
                <button.icon size={16} />
              </button>
            ) : (
              <div key={index} className="w-px bg-gray-400 mx-1"></div>
            )
          )}
        </div>
      )}
      
      <div
        ref={editorRef}
        contentEditable
        className="w-full min-h-[140px] p-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none appearance-none text-white leading-relaxed"
        style={{ fontFamily: 'inherit', fontSize: '16px', lineHeight: '1.5' }}
        suppressContentEditableWarning={true}
      />
      
      {!content && !isInitialized && (
        <div className="absolute top-3 left-3 text-gray-400 pointer-events-none">
          {placeholder}
        </div>
      )}

      {!scriptsLoaded && (
        <div className="text-xs text-yellow-400 mt-1">
          Loading PDF libraries...
        </div>
      )}
    </div>
  );
};

// Custom hook for knowledge base state management
const useKnowledgeBase = () => {
  const [state, setState] = useState({
    activeTab: 'list',
    knowledgeBases: [],
    expandedItems: [],
    selectedKnowledgeBaseIds: [],
    isLoading: false
  });

  const updateState = (updates) => setState(prev => ({ ...prev, ...updates }));

  const transformKnowledgeBaseData = (backendData) => {
    return backendData.map((item) => ({
      id: item.id,
      type: item.source_type === 'file' ? 'document' : 'url',
      url: item.source_url,
      urls: item.source_url ? [item.source_url] : [],
      dateAdded: new Date(item.created_at).toLocaleDateString(),
      status: item.ingestion_status || 'unknown',
      lastUpdateAttempt: item.last_update_attempt_at,
      lastUpdateStatus: item.last_update_status,
      updatedAt: item.updated_at,
      createdAt: item.created_at,
      originalFileName: item.original_file_name || null,
      fileSize: item.file_size || null,
      contentType: item.content_type || null,
      s3Key: item.s3_key || null,
      sourceType: item.source_type,
      sourceUrl: item.source_url,
      ingestionStatus: item.ingestion_status || 'unknown'
    }));
  };

  const fetchData = async () => {
    updateState({ isLoading: true });
    try {
      const response = await fetchKnowledgeBase();
      if (response.status === 'success' && response.client_knowledge_base) {
        updateState({ knowledgeBases: transformKnowledgeBaseData(response.client_knowledge_base) });
      } else {
        updateState({ knowledgeBases: [] });
      }
    } catch (error) {
      console.error('Error fetching knowledge base:', error);
      updateState({ knowledgeBases: [] });
    } finally {
      updateState({ isLoading: false });
    }
  };

  return { state, updateState, fetchData };
};

// Reusable components
const StatusBadge = ({ status }) => {
  const config = getStatusConfig(status);
  const StatusIcon = config.icon;
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs flex items-center space-x-1 ${config.color}`}>
      <StatusIcon size={12} className={status?.toLowerCase() === 'processing' ? 'animate-spin' : ''} />
      <span>{status || 'pending'}</span>
    </span>
  );
};

const KnowledgeBaseItem = ({ kb, isExpanded, isSelected, onToggleExpand, onToggleSelect, onDelete, onView }) => {
  const TypeIcon = getTypeIcon(kb.type);
  const isFile = kb.sourceType === 'file';
  
  return (
    <div className={`bg-gray-700 rounded-lg p-4 transition-colors ${isSelected ? 'ring-2 ring-blue-500 bg-gray-600' : ''}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onToggleSelect(kb.id, e.target.checked)}
            className="rounded border-gray-600 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
          />
          <TypeIcon size={20} className="text-blue-400" />
          <div>
            <div className="flex items-center space-x-2">
              <span className="text-white font-medium">
                {isFile ? (kb.originalFileName || 'Uploaded File') : 'URL Source'}
              </span>
              <StatusBadge status={kb.ingestionStatus} />
            </div>
            {!isFile && kb.url && <p className="text-sm text-gray-400 mt-1">Source: {kb.url}</p>}
            {isFile && kb.fileSize && <p className="text-sm text-gray-400 mt-1">Size: {formatFileSize(kb.fileSize)}</p>}
            {kb.contentType && <p className="text-sm text-gray-400 mt-1">Type: {kb.contentType}</p>}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {isFile && kb.sourceUrl && (
            <button onClick={() => onView(kb.sourceUrl, kb.originalFileName)} className="text-blue-400 hover:text-blue-300 transition-colors" title="View File">
              <Eye size={16} />
            </button>
          )}
          <button onClick={() => onToggleExpand(kb.id)} className="text-gray-400 hover:text-white transition-colors" title="Toggle Details">
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <button onClick={() => onDelete(kb.id)} className="text-red-400 hover:text-red-300 transition-colors" title="Delete Entry">
            <Trash2 size={16} />
          </button>
        </div>
      </div>
      
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-600">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-400">Added: {kb.dateAdded}</p>
              <p className="text-gray-400">Created: {new Date(kb.createdAt).toLocaleString()}</p>
              {kb.updatedAt && <p className="text-gray-400">Updated: {new Date(kb.updatedAt).toLocaleString()}</p>}
              <p className="text-gray-400">Source Type: {kb.sourceType}</p>
              <p className="text-gray-400">Ingestion Status: {kb.ingestionStatus}</p>
               {kb.sourceUrl && (
                  <p className="text-gray-400">
                    Source: {isFile && kb.sourceUrl.startsWith("https://s3") ? "File Upload" : kb.sourceUrl}
                  </p>
                )}
            </div>
            <div>
              {kb.lastUpdateAttempt && <p className="text-gray-400">Last Update Attempt: {new Date(kb.lastUpdateAttempt).toLocaleString()}</p>}
              {kb.lastUpdateStatus && <p className="text-gray-400">Last Update Status: {kb.lastUpdateStatus}</p>}
            </div>
          </div>
          
          {kb.sourceType !== 'file' && kb.urls && kb.urls.length > 0 && (
            <div className="mt-3">
              <p className="text-gray-400 mb-2">URLs:</p>
              <div className="space-y-1">
                {kb.urls.map((url, index) => (
                  <a key={index} href={url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 flex items-center space-x-1">
                    <ExternalLink size={12} />
                    <span>{url}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Modal = ({ isOpen, onClose, title, children, maxWidth = "max-w-2xl" }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-gray-800 rounded-lg p-6 ${maxWidth} w-full mx-4 overflow-y-auto`}>
        <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
        {children}
      </div>
    </div>
  );
};

const ActionButton = ({ onClick, disabled, variant = 'primary', children, className = '', type = 'button' }) => {
  const variants = {
    primary: "bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-500",
    secondary: "bg-gray-600 text-white hover:bg-gray-700",
    danger: "bg-red-500 text-white hover:bg-red-600",
    warning: "bg-orange-500 text-white hover:bg-orange-600"
  };
  
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`px-4 py-2 rounded-lg font-medium flex items-center space-x-2 disabled:cursor-not-allowed ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
};

export default function KnowledgeBasePage() {
  const { state, updateState, fetchData } = useKnowledgeBase();
  const [formData, setFormData] = useState({
    baseUrl: '', urls: '', content: '', file: null
  });
  const [formState, setFormState] = useState({
    activeType: 'url', isSubmitting: false,
    urlIngestionMethod: 'base_url', crawledUrls: [], selectedCrawledUrls: [],
    showCrawledUrlsModal: false, isCrawling: false, selectedFailedUrls: [],
    showReprocessModal: false, isReprocessing: false, isReinjecting: false,
    isViewing: false, isGeneratingPdf: false
  });
  const [alert, setAlert] = useState({
    isOpen: false, type: 'info', title: '', message: '', showConfirm: false, onConfirm: null
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const updateFormState = (updates) => setFormState(prev => ({ ...prev, ...updates }));
  const showAlert = (type, message, title = '', showConfirm = false, onConfirm = null) => {
    setAlert({ isOpen: true, type, title, message, showConfirm, onConfirm });
  };
  const closeAlert = () => setAlert(prev => ({ ...prev, isOpen: false }));

  useEffect(() => {
    fetchData().finally(() => setIsInitialLoad(false));
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const hasPending = state.knowledgeBases.some(kb => kb.status && ['pending', 'processing'].includes(kb.status.toLowerCase()));
      if (hasPending && !isInitialLoad) {
        fetchData(true);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [state.knowledgeBases, fetchData, isInitialLoad]);

  const createTextFileAsPDF = async (richContent, filename = 'text_content.pdf') => {
    try {
      updateFormState({ isGeneratingPdf: true });
      const initialPdf = await RichTextEditor.generatePdf(richContent);
      const initialBlob = initialPdf.output('blob');
      const initialSize = initialBlob.size;

      let finalPdf = initialPdf;
      let finalSize = initialSize;
      let compressionResult = null;

      // Silently compress if PDF is larger than target size
      if (initialSize > PDF_SIZE_LIMITS.TARGET_COMPRESSION_SIZE) {
        try {
          compressionResult = await RichTextEditor.compressPdf(richContent, PDF_SIZE_LIMITS.TARGET_COMPRESSION_SIZE);
          finalPdf = compressionResult.pdf;
          finalSize = compressionResult.size;
        } catch (compressionError) {
          console.error('PDF compression failed:', compressionError);
          // Continue with original PDF if compression fails
        }
      }

      const pdfBlob = finalPdf.output('blob');
      const file = new File([pdfBlob], filename, { type: 'application/pdf' });
      const response = await uploadFile(file);
      
      if (response.status === 'success') {
        await fetchData();
        return { success: true, compressionResult };
      }
      return { success: false, compressionResult: null };
    } catch (error) {
      console.error('Error creating PDF file:', error);
      updateFormState({ isGeneratingPdf: false });
      if (error.message.includes('PDF libraries not loaded')) {
        showAlert('error', 'PDF generation libraries are still loading. Please wait a moment and try again.');
      } else {
        showAlert('error', 'Error creating PDF file. Please try again.');
      }
      return { success: false, compressionResult: null };
    } finally {
      updateFormState({ isGeneratingPdf: false });
    }
  };

  const apiOperations = {
    async crawlBaseUrl(baseUrl) {
      updateFormState({ isCrawling: true });
      try {
        const response = await crawlBaseUrl(baseUrl);
        if (response.status === 'success' && response.crawled_urls) {
          const urlsArray = Object.entries(response.crawled_urls).map(([url, status]) => ({ url, status: 'success' }));
          
          if (urlsArray.length > 0) {
            const successfulUrls = urlsArray.filter(item => item.status === 'success').map(item => item.url);
            updateFormState({ crawledUrls: urlsArray, selectedCrawledUrls: successfulUrls, showCrawledUrlsModal: true });
            return true;
          }
        }
        showAlert('error', 'Failed to crawl base URL or no URLs found.');
        return false;
      } catch (error) {
        console.error('Error crawling base URL:', error);
        showAlert('error', 'Error crawling base URL. Please try again.');
        return false;
      } finally {
        updateFormState({ isCrawling: false });
      }
    },

    async ingestUrls(urls) {
      try {
        const response = await ingestUrls(urls);
        if (response.status === 'success') {
          await fetchData();
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error ingesting URLs:', error);
        return false;
      }
    },

    async uploadFile(file) {
      try {
        const response = await uploadFile(file);
        if (response.status === 'success') {
          await fetchData();
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error uploading file:', error);
        return false;
      }
    },

    async createTextFile(content, filename = 'text_content.pdf') {
      try {
        return await createTextFileAsPDF(content, filename);
      } catch (error) {
        console.error('Error creating PDF from rich text:', error);
        return { success: false, compressionResult: null };
      }
    },

    async deleteEntries(urls) {
      try {
        const response = await deleteKnowledgeBaseEntries(urls);
        if (response.status === 'success') {
          await fetchData();
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error deleting knowledge base entries:', error);
        return false;
      }
    },

    async viewFile(sourceUrl, filename) {
      updateFormState({ isViewing: true });
      try {
        const response = await downloadKnowledgeBaseFile(sourceUrl);
        if (response.status === 'success') {
          showAlert('success', 'File viewed successfully!');
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error viewing file:', error);
        showAlert('error', 'Error viewing file. Please try again.');
        return false;
      } finally {
        updateFormState({ isViewing: false });
      }
    }
  };

  const handlers = {
    toggleExpanded: (id) => {
      const expandedItems = state.expandedItems.includes(id)
        ? state.expandedItems.filter(item => item !== id)
        : [...state.expandedItems, id];
      updateState({ expandedItems });
    },

    toggleSelection: (id, isSelected) => {
      const selectedKnowledgeBaseIds = isSelected
        ? [...state.selectedKnowledgeBaseIds, id]
        : state.selectedKnowledgeBaseIds.filter(selectedId => selectedId !== id);
      updateState({ selectedKnowledgeBaseIds });
    },

    selectAll: () => updateState({ selectedKnowledgeBaseIds: state.knowledgeBases.map(kb => kb.id) }),
    clearSelection: () => updateState({ selectedKnowledgeBaseIds: [] }),

    async viewFile(sourceUrl, filename) {
      if (formState.isViewing || !sourceUrl) return;
      await apiOperations.viewFile(sourceUrl, filename);
    },

    async deleteKnowledgeBase(id) {
      const kb = state.knowledgeBases.find(kb => kb.id === id);
      
      showAlert('warning', 'Are you sure you want to delete this knowledge base entry?', 'Confirm Delete', true, async () => {
        if (kb && kb.urls && kb.urls.length > 0) {
          const success = await apiOperations.deleteEntries(kb.urls);
          if (success) showAlert('success', 'Knowledge base entry deleted successfully');
        } else {
          updateState({ knowledgeBases: state.knowledgeBases.filter(kb => kb.id !== id) });
        }
      });
    },

    async reinjectSelected() {
      const selectedKbs = state.knowledgeBases.filter(kb =>
        state.selectedKnowledgeBaseIds.includes(kb.id) && kb.sourceType === 'url'
      );
      
      if (selectedKbs.length === 0) {
        showAlert('warning', 'Please select at least one URL entry to reinject');
        return;
      }

      const urlsToReinject = selectedKbs.map(kb => kb.url).filter(Boolean);
      if (urlsToReinject.length === 0) {
        showAlert('warning', 'No valid URLs found in selected entries');
        return;
      }

      updateFormState({ isReinjecting: true });
      try {
        const success = await apiOperations.ingestUrls(urlsToReinject);
        if (success) {
          updateState({ selectedKnowledgeBaseIds: [] });
          showAlert('success', `Successfully reinjected ${urlsToReinject.length} URLs.`);
        }
      } finally {
        updateFormState({ isReinjecting: false });
      }
    },

    async deleteSelected() {
      if (state.selectedKnowledgeBaseIds.length === 0) {
        showAlert('warning', 'Please select at least one entry to delete');
        return;
      }

      showAlert('warning', `Are you sure you want to delete ${state.selectedKnowledgeBaseIds.length} selected entries?`, 'Confirm Delete', true, async () => {
        const selectedKbs = state.knowledgeBases.filter(kb => state.selectedKnowledgeBaseIds.includes(kb.id));
        const urlsToDelete = selectedKbs.map(kb => kb.url).filter(Boolean);
        
        if (urlsToDelete.length > 0) {
          const success = await apiOperations.deleteEntries(urlsToDelete);
          if (success) {
            updateState({ selectedKnowledgeBaseIds: [] });
            showAlert('success', 'Selected entries deleted successfully');
          }
        } else {
          updateState({
            knowledgeBases: state.knowledgeBases.filter(kb => !state.selectedKnowledgeBaseIds.includes(kb.id)),
            selectedKnowledgeBaseIds: []
          });
          showAlert('success', 'Selected entries deleted successfully');
        }
      });
    },

    resetForm: () => {
      setFormData({ baseUrl: '', urls: '', content: '', file: null });
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
    },

    async handleSubmit(e) {
      e.preventDefault();
      updateFormState({ isSubmitting: true });
      
      try {
        let success = false;
        let result = null;
        
        if (formState.activeType === 'url') {
          if (formState.urlIngestionMethod === 'base_url') {
            if (!formData.baseUrl.trim()) {
              showAlert('warning', 'Please enter a base URL');
              updateFormState({ isSubmitting: false });
              return;
            }
            updateFormState({ isSubmitting: false });
            success = await apiOperations.crawlBaseUrl(formData.baseUrl.trim());
            return;
          } else {
            if (!formData.urls.trim()) {
              showAlert('warning', 'Please enter URLs to ingest');
              updateFormState({ isSubmitting: false });
              return;
            }
            const urlsToIngest = formData.urls.split('\n').map(url => url.trim()).filter(url => url.length > 0);
            success = await apiOperations.ingestUrls(urlsToIngest);
          }
        } else if (formState.activeType === 'text') {
          if (!formData.content.trim()) {
            showAlert('warning', 'Please enter text content');
            updateFormState({ isSubmitting: false });
            return;
          }
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const filename = `text_content_${timestamp}.pdf`;
          result = await apiOperations.createTextFile(formData.content, filename);
          success = result.success;
        } else if (formState.activeType === 'document') {
          if (!formData.file) {
            showAlert('warning', 'Please select a file to upload');
            updateFormState({ isSubmitting: false });
            return;
          }
          if (formData.file.size > PDF_SIZE_LIMITS.MAX_SIZE) {
            showAlert('error', `The selected file (${formatFileSize(formData.file.size)}) exceeds the maximum allowed size of ${formatFileSize(PDF_SIZE_LIMITS.MAX_SIZE)}.`, 'File Too Large');
            updateFormState({ isSubmitting: false });
            return;
          }
          success = await apiOperations.uploadFile(formData.file);
        }

        if (success) {
          handlers.resetForm();
          updateState({ activeTab: 'list' });
          let successMessage = '';
          if (formState.activeType === 'text') {
            if (result?.compressionResult?.compressed) {
              const originalSize = result.compressionResult.originalSize || 0;
              const compressedSize = result.compressionResult.size || 0;
              if (originalSize > 0 && compressedSize > 0) {
                const compressionRatio = ((originalSize - compressedSize) / originalSize * 100).toFixed(1);
                successMessage = `Rich text content successfully compressed (${compressionRatio}% reduction) and saved to knowledge base!`;
              } else {
                successMessage = 'Rich text content successfully compressed and saved to knowledge base!';
              }
            } else {
              successMessage = 'Rich text content successfully saved to knowledge base!';
            }
          } else {
            successMessage = `${formState.activeType === 'url' ? 'URLs' : 'Document'} successfully ${formState.activeType === 'document' ? 'uploaded' : 'saved'} to knowledge base!`;
          }
          showAlert('success', successMessage);
        }
      } catch (error) {
        console.error('Error adding knowledge base:', error);
        showAlert('error', 'Error adding knowledge base. Please try again.');
      } finally {
        if (!(formState.activeType === 'url' && formState.urlIngestionMethod === 'base_url')) {
          updateFormState({ isSubmitting: false });
        }
      }
    }
  };

  const renderKnowledgeBaseList = () => {
    const failedKbs = state.knowledgeBases.filter(kb => kb.status?.toLowerCase() === 'failed' || kb.status?.toLowerCase() === 'error');
    const selectedUrlKbs = state.knowledgeBases.filter(kb => state.selectedKnowledgeBaseIds.includes(kb.id) && kb.sourceType === 'url');
    
    if (state.isLoading && isInitialLoad) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="ml-4 text-gray-400">Loading knowledge base...</p>
        </div>
      );
    }

    const sortedKnowledgeBases = [...state.knowledgeBases].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return (
      <div className="space-y-6">
        {/* Multi-select Actions Bar */}
        {state.selectedKnowledgeBaseIds.length > 0 && (
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-blue-300 font-medium">{state.selectedKnowledgeBaseIds.length} selected</span>
                <button onClick={handlers.clearSelection} className="text-blue-400 hover:text-blue-300 text-sm underline">
                  Clear selection
                </button>
              </div>
              <div className="flex items-center space-x-3">
                {selectedUrlKbs.length > 0 && (
                  <ActionButton
                    onClick={handlers.reinjectSelected}
                    disabled={formState.isReinjecting}
                    variant="primary"
                    className="bg-purple-500 hover:bg-purple-600"
                  >
                    {formState.isReinjecting && <RefreshCw size={16} className="animate-spin" />}
                    <span>{formState.isReinjecting ? 'Reinjecting...' : `Reinject URLs (${selectedUrlKbs.length})`}</span>
                  </ActionButton>
                )}
                <ActionButton onClick={handlers.deleteSelected} variant="danger">
                  <Trash2 size={16} />
                  <span>Delete Selected ({state.selectedKnowledgeBaseIds.length})</span>
                </ActionButton>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white">Knowledge Base Sources</h2>
          <div className="flex space-x-3">
            {state.knowledgeBases.length > 0 && (
              <div className="flex items-center space-x-2">
                <button onClick={handlers.selectAll} className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700">
                  Select All
                </button>
                {state.selectedKnowledgeBaseIds.length > 0 && (
                  <button onClick={handlers.clearSelection} className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700">
                    Clear All
                  </button>
                )}
              </div>
            )}
            {failedKbs.length > 0 && (
              <ActionButton onClick={() => updateFormState({ showReprocessModal: true })} variant="warning">
                <RefreshCw size={16} />
                <span>Reprocess Failed ({failedKbs.length})</span>
              </ActionButton>
            )}
            <ActionButton onClick={() => fetchData()}>
              <RefreshCw size={16} />
              <span>Refresh</span>
            </ActionButton>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6">
          {sortedKnowledgeBases.length === 0 && !state.isLoading ? (
            <div className="text-center py-12">
              <Book size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No knowledge base sources found</h3>
              <p className="text-gray-400">Add your first knowledge base source to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedKnowledgeBases.map((kb) => (
                <KnowledgeBaseItem
                  key={kb.id}
                  kb={kb}
                  isExpanded={state.expandedItems.includes(kb.id)}
                  isSelected={state.selectedKnowledgeBaseIds.includes(kb.id)}
                  onToggleExpand={handlers.toggleExpanded}
                  onToggleSelect={handlers.toggleSelection}
                  onDelete={handlers.deleteKnowledgeBase}
                  onView={handlers.viewFile}
                />
              ))}
            </div>
          )}
        </div>

        <Modal
          isOpen={formState.showReprocessModal}
          onClose={() => updateFormState({ showReprocessModal: false, selectedFailedUrls: [] })}
          title="Reprocess Failed URLs"
        >
          <p className="text-gray-400 mb-4">Select the URLs you want to reprocess:</p>
          
          <div className="space-y-2 mb-6">
            {failedKbs.map((kb) => (
              kb.urls.map((url, index) => (
                <div key={`${kb.id}-${index}`} className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id={`url-${kb.id}-${index}`}
                    checked={formState.selectedFailedUrls.includes(url)}
                    onChange={(e) => {
                      const newSelected = e.target.checked
                        ? [...formState.selectedFailedUrls, url]
                        : formState.selectedFailedUrls.filter(u => u !== url);
                      updateFormState({ selectedFailedUrls: newSelected });
                    }}
                    className="rounded border-gray-600 text-blue-500"
                  />
                  <label htmlFor={`url-${kb.id}-${index}`} className="text-sm text-gray-300 flex-1">
                    {url}
                  </label>
                  <span className="text-xs text-red-400">Failed</span>
                </div>
              ))
            ))}
          </div>
          
          <div className="flex justify-end space-x-3">
            <ActionButton
              onClick={() => updateFormState({ showReprocessModal: false, selectedFailedUrls: [] })}
              variant="secondary"
              disabled={formState.isReprocessing}
            >
              Cancel
            </ActionButton>
            <ActionButton
              onClick={async () => {
                if (formState.selectedFailedUrls.length === 0) {
                  showAlert('warning', 'Please select URLs to reprocess');
                  return;
                }
                updateFormState({ isReprocessing: true });
                try {
                  const success = await apiOperations.ingestUrls(formState.selectedFailedUrls);
                  if (success) {
                    showAlert('success', `Successfully reprocessed ${formState.selectedFailedUrls.length} URLs`);
                    updateFormState({ selectedFailedUrls: [], showReprocessModal: false });
                  }
                } finally {
                  updateFormState({ isReprocessing: false });
                }
              }}
              disabled={formState.selectedFailedUrls.length === 0 || formState.isReprocessing}
              variant="warning"
            >
              {formState.isReprocessing && <RefreshCw size={16} className="animate-spin" />}
              <span>{formState.isReprocessing ? 'Reprocessing...' : `Reprocess Selected (${formState.selectedFailedUrls.length})`}</span>
            </ActionButton>
          </div>
        </Modal>
      </div>
    );
  };

  const renderAddKnowledgeBase = () => (
    <div className="bg-gray-800 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-white mb-6">Add Knowledge Base Source</h3>
      
      <form onSubmit={handlers.handleSubmit} className="space-y-6">
        {/* Source Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">Source Type</label>
          <select
            value={formState.activeType}
            onChange={(e) => updateFormState({ activeType: e.target.value })}
            className="bg-gray-700 border border-gray-600 text-gray-300 text-sm rounded-lg block w-full p-2.5 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="url">URL</option>
            <option value="document">Document</option>
            <option value="text">Rich Text</option>
          </select>
        </div>

        {/* URL Form */}
        {formState.activeType === 'url' && (
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-2">URL Ingestion Method</label>
            <div className="space-y-4">
              <div className="flex space-x-4">
                {[
                  { method: 'base_url', label: 'Base URL (Crawl & Select)' },
                  { method: 'manual_urls', label: 'Manual URLs' }
                ].map(({ method, label }) => (
                  <button
                    key={method}
                    type="button"
                    onClick={() => updateFormState({ urlIngestionMethod: method })}
                    className={`px-4 py-2 rounded-lg border ${
                      formState.urlIngestionMethod === method
                        ? 'bg-blue-500 border-blue-500 text-white'
                        : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              
              {formState.urlIngestionMethod === 'base_url' ? (
                <div className="space-y-3">
                  <input
                    type="url"
                    placeholder="Enter base URL (e.g., https://example.com)"
                    value={formData.baseUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, baseUrl: e.target.value }))}
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required={formState.activeType === 'url' && formState.urlIngestionMethod === 'base_url'}
                  />
                  <p className="text-sm text-gray-400">
                    The base URL will be crawled to find all pages, then you can select which ones to ingest.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <textarea
                    placeholder="Enter URLs (one per line)"
                    value={formData.urls}
                    onChange={(e) => setFormData(prev => ({ ...prev, urls: e.target.value }))}
                    rows={5}
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required={formState.activeType === 'url' && formState.urlIngestionMethod === 'manual_urls'}
                  />
                  <p className="text-sm text-gray-400">
                    Enter each URL on a separate line. All URLs will be processed individually.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {formState.activeType === 'text' && (
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-2">Rich Text Content</label>
            <RichTextEditor 
              content={formData.content}
              setContent={(content) => setFormData(prev => ({ ...prev, content }))}
              placeholder="Enter or paste your rich text content here..."
            />
          </div>
        )}

        {/* Document Form */}
        {formState.activeType === 'document' && (
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-2">Document</label>
            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-700 hover:bg-gray-600 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <UploadCloud size={24} className="text-gray-400 mb-2" />
                  <p className="text-sm text-gray-400">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">DOC, DOCX, TXT, PDF (MAX. {formatFileSize(PDF_SIZE_LIMITS.MAX_SIZE)})</p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const allowedTypes = ['.doc', '.docx', '.txt', '.pdf'];
                      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
                      
                      if (allowedTypes.includes(fileExtension)) {
                        if (file.size > PDF_SIZE_LIMITS.MAX_SIZE) {
                          showAlert('error', `The selected file (${formatFileSize(file.size)}) exceeds the maximum allowed size of ${formatFileSize(PDF_SIZE_LIMITS.MAX_SIZE)}.`, 'File Too Large');
                          e.target.value = '';
                          return;
                        }
                        setFormData(prev => ({ ...prev, file }));
                      } else {
                        showAlert('error', 'Invalid file type. Only DOC, DOCX, TXT, and PDF files are allowed.');
                        e.target.value = '';
                      }
                    }
                  }}
                  accept=".doc,.docx,.txt,.pdf"
                />
              </label>
            </div>
            {formData.file && (
              <div className="mt-2 p-2 bg-gray-700 rounded text-sm text-gray-300 flex items-center space-x-2">
                <FileText size={16} />
                <span>Selected: {formData.file.name} ({formatFileSize(formData.file.size)})</span>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, file: null }))}
                  className="text-red-400 hover:text-red-300 ml-auto"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Submit Button */}
        <ActionButton
          onClick={() => {}}
          disabled={formState.isSubmitting || formState.isCrawling || formState.isGeneratingPdf}
          className="w-full"
          type="submit"
        >
          {(formState.isSubmitting || formState.isCrawling || formState.isGeneratingPdf) && <RefreshCw size={16} className="animate-spin" />}
          <span>
            {formState.isCrawling
              ? 'Crawling URLs...'
              : formState.isGeneratingPdf
                ? 'Generating & Compressing PDF...'
                : formState.isSubmitting
                  ? 'Processing...'
                  : formState.urlIngestionMethod === 'base_url' && formState.activeType === 'url'
                    ? 'Crawl & Select URLs'
                    : 'Save to Knowledge Base'
            }
          </span>
        </ActionButton>
      </form>
      
      <Modal
        isOpen={formState.showCrawledUrlsModal}
        onClose={() => updateFormState({
          showCrawledUrlsModal: false,
          crawledUrls: [],
          selectedCrawledUrls: []
        })}
        title="Select URLs to Ingest"
        maxWidth="max-w-4xl"
      >
        <p className="text-gray-400 mb-4">
          Found {formState.crawledUrls.length} URLs from crawling. Select which ones you want to add to your knowledge base:
        </p>
        
        <div className="flex justify-between items-center mb-4">
          <div className="flex space-x-3">
            <button
              onClick={() => {
                const successfulUrls = formState.crawledUrls
                  .filter(item => item.status === 'success')
                  .map(item => item.url);
                updateFormState({ selectedCrawledUrls: successfulUrls });
              }}
              className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
            >
              Select All Successful ({formState.crawledUrls.filter(item => item.status === 'success').length})
            </button>
            <button
              onClick={() => updateFormState({ selectedCrawledUrls: [] })}
              className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
            >
              Deselect All
            </button>
          </div>
          <span className="text-sm text-gray-400">
            Selected: {formState.selectedCrawledUrls.length} / {formState.crawledUrls.length}
          </span>
        </div>
        
        <div className="space-y-2 mb-6 max-h-64 overflow-y-auto">
          {formState.crawledUrls.map((item, index) => (
            <div key={index} className="flex items-center space-x-3 p-2 rounded bg-gray-700">
              <input
                type="checkbox"
                id={`crawled-url-${index}`}
                checked={formState.selectedCrawledUrls.includes(item.url)}
                onChange={(e) => {
                  const newSelected = e.target.checked
                    ? [...formState.selectedCrawledUrls, item.url]
                    : formState.selectedCrawledUrls.filter(u => u !== item.url);
                  updateFormState({ selectedCrawledUrls: newSelected });
                }}
                className="rounded border-gray-600 text-blue-500"
                disabled={item.status === 'failed'}
              />
              <label htmlFor={`crawled-url-${index}`} className="text-sm text-gray-300 flex-1">
                {item.url}
              </label>
              <span className={`text-xs px-2 py-1 rounded ${
                item.status === 'success'
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-red-500/20 text-red-400'
              }`}>
                {item.status}
              </span>
            </div>
          ))}
        </div>
        
        <div className="flex justify-end space-x-3">
          <ActionButton
            onClick={() => {
              updateFormState({
                showCrawledUrlsModal: false,
                crawledUrls: [],
                selectedCrawledUrls: []
              });
              handlers.resetForm();
            }}
            variant="secondary"
            disabled={formState.isSubmitting}
          >
            Cancel
          </ActionButton>
          <ActionButton
            onClick={async () => {
              if (formState.selectedCrawledUrls.length === 0) {
                showAlert('warning', 'Please select at least one URL to ingest');
                return;
              }
              updateFormState({ isSubmitting: true });
              try {
                const success = await apiOperations.ingestUrls(formState.selectedCrawledUrls);
                if (success) {
                  updateFormState({
                    showCrawledUrlsModal: false,
                    crawledUrls: [],
                    selectedCrawledUrls: []
                  });
                  handlers.resetForm();
                  updateState({ activeTab: 'list' });
                  showAlert('success', `Successfully ingested ${formState.selectedCrawledUrls.length} URLs into knowledge base!`);
                }
              } finally {
                updateFormState({ isSubmitting: false });
              }
            }}
            disabled={formState.selectedCrawledUrls.length === 0 || formState.isSubmitting}
          >
            {formState.isSubmitting && <RefreshCw size={16} className="animate-spin" />}
            <span>
              {formState.isSubmitting ? 'Ingesting...' : `Ingest Selected (${formState.selectedCrawledUrls.length})`}
            </span>
          </ActionButton>
        </div>
      </Modal>
    </div>
  );

  return (
    <div className="bg-gray-900 text-white p-6">
      <div className="max-w-6xl mx-auto">
        <header className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Knowledge Base</h1>
          <p className="text-gray-400">Manage your knowledge base sources and content</p>
        </header>
        
        <div className="flex space-x-4 mb-8">
          {[
            { tab: 'list', label: 'Knowledge Base List', icon: FolderOpen },
            { tab: 'add', label: 'Add Source', icon: Plus }
          ].map(({ tab, label, icon: Icon }) => (
            <button
              key={tab}
              onClick={() => updateState({ activeTab: tab })}
              className={`px-4 py-2 rounded-lg font-medium flex items-center space-x-2 ${
                state.activeTab === tab
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <Icon size={16} />
              <span>{label}</span>
            </button>
          ))}
        </div>
        
        {state.activeTab === 'list' ? renderKnowledgeBaseList() : renderAddKnowledgeBase()}
        
        <CustomAlert
          isOpen={alert.isOpen}
          onClose={closeAlert}
          type={alert.type}
          title={alert.title}
          message={alert.message}
          showConfirm={alert.showConfirm}
          onConfirm={alert.onConfirm}
        />
        
        {/* View Loading Indicator */}
        {formState.isViewing && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40">
            <div className="bg-gray-800 rounded-lg p-6 flex items-center space-x-4">
              <RefreshCw size={24} className="animate-spin text-blue-400" />
              <span className="text-white">Viewing file...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
